// frontend/src/features/livestock/EditTimelineEntryModal.tsx
/**
 * Modal for editing an existing timeline entry's text and date.
 * Photos are displayed read-only (backend PUT doesn't support photo changes).
 * Tank shots use it too: they have the same date, note and photo fields.
 */

import { useEffect, useState } from 'react';
import {
  Modal,
  Textarea,
  Button,
  Group,
  Stack,
  Image,
  Text,
  Box,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import type { TimelineEntryUpdate } from '@/types/timeline';

/** What the form edits: an organism timeline entry or a tank shot. */
export interface EditableEntry {
  id: number;
  entry_text: string | null;
  entry_date: string;
  photo_url: string | null;
  photo_thumb_url: string | null;
}

export interface EditTimelineEntryModalProps {
  entry: EditableEntry | null;
  opened: boolean;
  onClose: () => void;
  onSave: (entryId: number, data: TimelineEntryUpdate) => Promise<void>;
  isSaving: boolean;
  /** Defaults to "Edit Entry". */
  title?: string;
  /** Earliest and latest dates the server accepts, when it limits them. */
  minDate?: Date;
  maxDate?: Date;
  /** Above another modal (the photo lightbox). */
  zIndex?: number;
}

/** The picker shows and edits minutes: an unchanged date compares equal at that precision. */
function sameMinute(a: Date, b: Date): boolean {
  return Math.floor(a.getTime() / 60000) === Math.floor(b.getTime() / 60000);
}

export function EditTimelineEntryModal({
  entry,
  opened,
  onClose,
  onSave,
  isSaving,
  title = 'Edit Entry',
  minDate,
  maxDate,
  zIndex,
}: EditTimelineEntryModalProps): JSX.Element | null {
  const [entryText, setEntryText] = useState('');
  const [entryDate, setEntryDate] = useState<Date | null>(null);

  // Reset form when entry changes or modal opens
  useEffect(() => {
    if (entry && opened) {
      setEntryText(entry.entry_text ?? '');
      setEntryDate(new Date(entry.entry_date));
    }
  }, [entry, opened]);

  const hasPhoto = !!entry?.photo_url;
  const hasContent = entryText.trim().length > 0 || hasPhoto;
  const hasValidDate = entryDate !== null;

  const handleSave = async (): Promise<void> => {
    if (!entry || !entryDate) return;

    const data: TimelineEntryUpdate = { entry_text: entryText.trim() || null };
    // Only a date the user changed is sent: the server clears "date from the photo"
    // (taken_at_source) when the date differs, and a round trip through Date loses
    // the stored microseconds, so resending an unchanged date would wipe it.
    if (!sameMinute(entryDate, new Date(entry.entry_date))) {
      data.entry_date = entryDate.toISOString();
    }
    await onSave(entry.id, data);
  };

  if (!entry) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="md"
      centered
      zIndex={zIndex}
    >
      <Stack gap="sm">
        {/* Read-only photo preview */}
        {entry.photo_thumb_url && (
          <Box>
            <Image
              src={entry.photo_thumb_url}
              alt="Entry photo"
              w={120}
              h={120}
              fit="cover"
              radius="md"
            />
            <Text size="xs" c="dimmed" mt={4}>
              Photo cannot be changed
            </Text>
          </Box>
        )}

        <Textarea
          label="Note"
          value={entryText}
          onChange={(e) => setEntryText(e.currentTarget.value)}
          minRows={3}
          maxRows={8}
          autosize
          disabled={isSaving}
        />

        <DateTimePicker
          label="Date"
          value={entryDate}
          onChange={(val: string | Date | null) =>
            setEntryDate(val ? (typeof val === 'string' ? new Date(val) : val) : null)
          }
          disabled={isSaving}
          minDate={minDate}
          maxDate={maxDate}
        />

        <Group justify="flex-end" mt="sm">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasContent || !hasValidDate || isSaving}
            loading={isSaving}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
